// "+ New account" modal: create a company without leaving the current
// screen (AddSecurityModal pattern). Essentials only — everything else is
// edited later on the company form. Role is REQUIRED (owner rule: every
// company create takes a role) and submits the same
// prog__account_roles insert-action entries the drawer sends. City /
// Country use the shared CitySelect / CountrySelect so the business
// address carries the code columns, not just labels. Submits through
// createCompanyService so the Redux addCompany dispatch and activeRecords
// middleware behave exactly as the drawer create does.
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input, Modal, Select } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { createCompanyService } from '../../../state_manager/state_update_actions/companies/company_services.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { AccountRoleEntryFields } from '../../../state_manager/data_models/account_role_entry.ts'
import { buildAccountRoleOptions } from '../../../core/utils/accountRoleOptions.ts'
import { CitySelect } from '../../../core/components/InputManager/components/CitySelect.tsx'
import { CountrySelect } from '../../../core/components/InputManager/components/CountrySelect.tsx'
import { selectLookupOptions } from '../../global_configs/hooks/useLookups.ts'
import { LOOKUP_CATEGORY_INDUSTRY } from '../../../state_manager/constants/lookupCategories.ts'

// House form ergonomics: Enter commits the field (blurs).
const blurOnEnter = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') (e.target as HTMLElement).blur()
}

interface AddCompanyModalProps {
  open: boolean
  // accountName lets callers append the new option locally without a refetch
  onCreated: (accountId: string, accountName: string) => void | Promise<void>
  onCancel: () => void
}

export default function AddCompanyModal({
  open,
  onCreated,
  onCancel
}: AddCompanyModalProps): React.ReactElement {
  const dispatch = useDispatch()
  const industryOptions = useSelector((state: any) =>
    selectLookupOptions(state, LOOKUP_CATEGORY_INDUSTRY)
  )
  const accountRoleChoices = useSelector(
    (state: any) => state.globalConfigs?.account_role_choices || []
  )
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [legalName, setLegalName] = useState('')
  const [roleIds, setRoleIds] = useState<string[]>([])
  const [industry, setIndustry] = useState<string | null>(null)
  const [website, setWebsite] = useState('')
  const [domain, setDomain] = useState('')
  const [address1, setAddress1] = useState('')
  const [address2, setAddress2] = useState('')
  const [cityCode, setCityCode] = useState<string | null>(null)
  const [cityLabel, setCityLabel] = useState<string | null>(null)
  const [stateProvince, setStateProvince] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [countryCode, setCountryCode] = useState<string | null>(null)
  const [countryLabel, setCountryLabel] = useState<string | null>(null)

  const reset = (): void => {
    setName('')
    setLegalName('')
    setRoleIds([])
    setIndustry(null)
    setWebsite('')
    setDomain('')
    setAddress1('')
    setAddress2('')
    setCityCode(null)
    setCityLabel(null)
    setStateProvince('')
    setPostalCode('')
    setCountryCode(null)
    setCountryLabel(null)
  }

  const handleOk = async (): Promise<void> => {
    if (!name.trim()) {
      showErrorToast('Company name is required')
      return
    }
    if (roleIds.length === 0) {
      showErrorToast('Role is required')
      return
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      // Optional fields are OMITTED when empty (not sent as null) — the
      // backend account model accepts absent keys, not explicit nulls.
      const payload: Record<string, any> = {
        // Placeholder id — required by the backend record schema; the
        // create endpoint generates and uses its own (drawer convention).
        [AccountRecordFields.accounts__id]: crypto.randomUUID(),
        [AccountRecordFields.accounts__name]: name.trim(),
        // Same insert-action entries the drawer's role transformer emits.
        prog__account_roles: roleIds.map((roleChoiceId) => ({
          [AccountRoleEntryFields.account_roles__id]: crypto.randomUUID(),
          [AccountRoleEntryFields.account_roles__account_role_choice_id]: roleChoiceId,
          [AccountRoleEntryFields.prog__account_roles__action]: 'insert'
        }))
      }
      const put = (key: string, value: any): void => {
        if (value !== null && value !== undefined && value !== '') payload[key] = value
      }
      put(AccountRecordFields.accounts__legal_name, legalName.trim())
      put(AccountRecordFields.accounts__industry, industry)
      put(AccountRecordFields.accounts__website, website.trim())
      put(AccountRecordFields.accounts__domain, domain.trim())
      put(AccountRecordFields.accounts__business_address_1, address1.trim())
      put(AccountRecordFields.accounts__business_address_2, address2.trim())
      put(AccountRecordFields.accounts__business_city_code, cityCode)
      put(AccountRecordFields.accounts__business_city_label, cityLabel)
      put(AccountRecordFields.accounts__business_stateprovince, stateProvince.trim())
      put(AccountRecordFields.accounts__business_postalcode, postalCode.trim())
      put(AccountRecordFields.accounts__business_country_code, countryCode)
      put(AccountRecordFields.accounts__business_country_label, countryLabel)
      const result = await createCompanyService(payload, token, dispatch as any)
      const accountId = (result.data as any)?.[AccountRecordFields.accounts__id]
      if (result.error || !accountId) {
        showErrorToast(result.error || 'Failed to create company')
        return
      }
      toast.success('Company created')
      await onCreated(accountId, name.trim())
      reset()
    } finally {
      setSaving(false)
    }
  }

  const field = (label: string, control: React.ReactNode, required = false) => (
    <div className='flex items-center gap-2 mb-2'>
      <div className='w-32 shrink-0 text-right text-gray-500 text-sm'>
        {required ? <span className='text-red-500'>* </span> : null}
        {label}
      </div>
      <div className='flex-1 min-w-0'>{control}</div>
    </div>
  )

  return (
    <Modal
      title='New company'
      open={open}
      onOk={handleOk}
      okText='Create company'
      confirmLoading={saving}
      onCancel={() => {
        reset()
        onCancel()
      }}
      width={560}
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      destroyOnClose
    >
      {field(
        'Name',
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={blurOnEnter}
          placeholder='Company name'
        />,
        true
      )}
      {field(
        'Legal name',
        <Input
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          onKeyDown={blurOnEnter}
          placeholder='Optional'
        />
      )}
      {field(
        'Role',
        <Select
          className='w-full'
          mode='multiple'
          value={roleIds}
          options={buildAccountRoleOptions(accountRoleChoices)}
          placeholder='Select role(s)…'
          allowClear
          showSearch
          optionFilterProp='label'
          onChange={(v) => setRoleIds((v as string[]) ?? [])}
        />,
        true
      )}
      {field(
        'Industry',
        <Select
          className='w-full'
          value={industry ?? undefined}
          options={industryOptions}
          placeholder='Optional'
          allowClear
          showSearch
          optionFilterProp='label'
          onChange={(v) => setIndustry(v ?? null)}
        />
      )}
      {field(
        'Website',
        <Input
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          onKeyDown={blurOnEnter}
          placeholder='Optional'
        />
      )}
      {field(
        'Domain',
        <Input
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          onKeyDown={blurOnEnter}
          placeholder='Optional'
        />
      )}
      <div className='text-gray-400 text-xs mt-3 mb-2'>Business Address</div>
      {field(
        'Street address 1',
        <Input
          value={address1}
          onChange={(e) => setAddress1(e.target.value)}
          onKeyDown={blurOnEnter}
        />
      )}
      {field(
        'Street address 2',
        <Input
          value={address2}
          onChange={(e) => setAddress2(e.target.value)}
          onKeyDown={blurOnEnter}
        />
      )}
      {field(
        'City',
        <CitySelect
          className='w-full'
          value={cityLabel ?? undefined}
          country={countryCode}
          onSelect={(value: string, option: any) => {
            setCityCode(value ?? null)
            setCityLabel(option?.name || option?.label || null)
            // A city pick also pins its country (code AND display label,
            // mapped via globalConfigs.countries) and state, drawer-style.
            const pickedCountry = option?.data?.countryCode
            if (pickedCountry) {
              setCountryCode(pickedCountry)
              const pickedLabel = countriesData.find(
                (country: any) => country.code === pickedCountry
              )?.name
              if (pickedLabel) setCountryLabel(pickedLabel)
            }
            const pickedState = option?.data?.state
            if (pickedState) setStateProvince(pickedState)
          }}
          onClear={() => {
            setCityCode(null)
            setCityLabel(null)
          }}
        />
      )}
      {field(
        'State / Province',
        <Input
          value={stateProvince}
          onChange={(e) => setStateProvince(e.target.value)}
          onKeyDown={blurOnEnter}
        />
      )}
      {field(
        'ZIP code',
        <Input
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          onKeyDown={blurOnEnter}
        />
      )}
      {field(
        'Country',
        <CountrySelect
          className='w-full'
          value={countryLabel ?? undefined}
          onSelect={(value: string, option: any) => {
            setCountryCode(value ?? null)
            setCountryLabel(option?.label || option?.name || null)
          }}
          onClear={() => {
            setCountryCode(null)
            setCountryLabel(null)
          }}
        />
      )}
    </Modal>
  )
}
